// File: src/components/admin/UserDetail/UserRateLimitsTab.js
// Per-user rate-limit / block state tab. Surfaces every active
// bucket — including the mitigation ("ban") buckets that are invisible in the
// normal quota — and lets an admin reset them all in one click to unblock a
// stuck user without waiting for the UTC-midnight day-bucket reset.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Alert, CircularProgress, Button, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack,
} from '@mui/material';
import { Refresh, LockReset, Block, WarningAmber } from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const TYPE_LABELS = {
    mitigation: 'Ban (mitigation)',
    day: 'Daily quota',
    minute: 'Per-minute (token)',
    request: 'Request window',
};

const formatReset = (isoStr) => {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    // Show both local and UTC: the day bucket resets at UTC midnight, which is
    // the most common "why am I still blocked?" confusion.
    return `${d.toLocaleString()} (${d.toISOString().replace('T', ' ').replace('.000Z', ' UTC')})`;
};

const BucketTypeChip = ({ type }) => (
    <Chip
        size="small"
        label={TYPE_LABELS[type] || type}
        color={type === 'mitigation' ? 'error' : type === 'day' ? 'warning' : 'default'}
        variant={type === 'mitigation' ? 'filled' : 'outlined'}
        data-testid={`rate-limit-bucket-type-${type}`}
    />
);

const UserRateLimitsTab = ({ uid, onStatusChange }) => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [resultMsg, setResultMsg] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminApi.getUserRateLimits(uid);
            setStatus(data);
            onStatusChange?.(data);
        } catch (err) {
            setError(handleAdminApiError(err));
        } finally {
            setLoading(false);
        }
    }, [uid, onStatusChange]);

    useEffect(() => { load(); }, [load]);

    const handleReset = async () => {
        setResetting(true);
        setResultMsg(null);
        try {
            const res = await adminApi.resetUserRateLimits(uid);
            setResultMsg({ severity: 'success', text: `Cleared ${res.clearedKeys} rate-limit key(s). User unblocked.` });
            setConfirmOpen(false);
            await load();
        } catch (err) {
            setResultMsg({ severity: 'error', text: handleAdminApiError(err) });
        } finally {
            setResetting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }} data-testid="rate-limits-loading">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>} data-testid="rate-limits-error">
                {error}
            </Alert>
        );
    }

    const buckets = status?.buckets || [];
    const hasBuckets = buckets.length > 0;

    return (
        <Box data-testid="rate-limits-tab">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">Rate Limits</Typography>
                    {status?.blocked ? (
                        <Chip icon={<Block />} color="error" label="Blocked" data-testid="rate-limits-blocked-chip" />
                    ) : (
                        <Chip color="success" variant="outlined" label="Not blocked" data-testid="rate-limits-ok-chip" />
                    )}
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button startIcon={<Refresh />} onClick={load} data-testid="rate-limits-refresh-btn">
                        Refresh
                    </Button>
                    <Tooltip title={hasBuckets ? 'Clear all rate-limit buckets for this user' : 'Nothing to reset'}>
                        <span>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<LockReset />}
                                disabled={!hasBuckets}
                                onClick={() => setConfirmOpen(true)}
                                data-testid="rate-limits-reset-btn"
                            >
                                Reset rate limits
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {resultMsg && (
                <Alert severity={resultMsg.severity} sx={{ mb: 2 }} onClose={() => setResultMsg(null)} data-testid="rate-limits-result">
                    {resultMsg.text}
                </Alert>
            )}

            {status && status.available === false && (
                <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }} data-testid="rate-limits-unavailable">
                    Rate-limit store is currently unavailable — status could not be read. Try again shortly.
                </Alert>
            )}

            {!hasBuckets ? (
                <Alert severity="info" data-testid="rate-limits-empty">
                    No active rate limits for this user.
                </Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined" data-testid="rate-limits-table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Bucket</TableCell>
                                <TableCell>Scope</TableCell>
                                <TableCell>Endpoint</TableCell>
                                <TableCell align="right">Count / Limit</TableCell>
                                <TableCell>Resets at</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {buckets.map((b) => (
                                <TableRow key={b.key} data-testid="rate-limits-row">
                                    <TableCell><BucketTypeChip type={b.type} /></TableCell>
                                    <TableCell>{b.scope}</TableCell>
                                    <TableCell>{b.endpoint || '—'}</TableCell>
                                    <TableCell align="right">
                                        {b.type === 'mitigation'
                                            ? 'banned'
                                            : `${b.count ?? '—'} / ${b.limit || '∞'}`}
                                    </TableCell>
                                    <TableCell>{formatReset(b.resetAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={confirmOpen} onClose={() => !resetting && setConfirmOpen(false)} data-testid="rate-limits-confirm-dialog">
                <DialogTitle>Reset rate limits?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This clears <strong>all {buckets.length}</strong> rate-limit and ban bucket(s) for this user,
                        immediately unblocking them. This cannot be undone (counters restart from zero).
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={resetting} data-testid="rate-limits-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReset}
                        color="error"
                        variant="contained"
                        disabled={resetting}
                        startIcon={resetting ? <CircularProgress size={16} color="inherit" /> : <LockReset />}
                        data-testid="rate-limits-confirm-btn"
                    >
                        {resetting ? 'Resetting…' : 'Reset'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserRateLimitsTab;
